import React from 'react';

import {ProcessExecutionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {LookupStepType} from 'lookup-config/LookupConfigModalConstants';
import {ProcessSchemaFieldTableProps} from 'processes/settings/ProcessTaskSchemaEditor';
import type {FieldTableQuery} from 'registry/field/__generated/FieldTableQuery';
import {getFieldOptions} from 'registry/field/FieldTableOptionGetters';
import FieldTableRow, {DisablableControl} from 'registry/field/FieldTableRow';
import {getSnapshotFieldDisabledMessage} from 'registry/field/FieldUtil';
import {AssaySchema} from 'results/assay-schemas/AssaySchemaConstants';
import {StorableSchema} from 'samples/storables/StorableSchemaConstants';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {SchemaInterface} from 'schemas/SchemaConstants';
import {getSnapshotFieldLookupConfigModalProps} from 'schemas/SnapshotFieldLookupConfigUtil';

import {TagSchemaSummaryGraphQL} from '../../../graphql-fragments/schemas/tag-schemas/__generated/TagSchemaSummaryGraphQL';

type FieldTableQuerySelector = FieldTableQuery['schemaFieldSelectors'][number];

const DISALLOW_DELETE_SCHEMA_FIELDS = 'Workflow schema fields cannot be deleted; they can only be archived.';

export interface ProcessSchemaFieldTableRowProps {
  tagSchemas: Array<TagSchemaSummaryGraphQL>;
  storableSchemas: Array<StorableSchema>;
  assaySchemas: Array<AssaySchema>;
  schemaInterfaces: Array<SchemaInterface>;
  schemaFieldSelectors: Array<FieldTableQuerySelector>;
  id: string;
  initialField: SchemaField;
  onCancel: (id: string) => void;
  onEdit: (id: string) => void;
  requestChange: (id: string, fieldWithChanges: SchemaField) => void;
  requestRemove: (id: string) => void;
  ownerUrlId: string;
  registryId?: string | null;
  schema: ProcessSchemaFieldTableProps['schema'];
  isFieldBeingCreated: boolean;
  schemaType?: string;
  compactMode?: boolean;
}

export default function ProcessSchemaFieldTableRow(props: ProcessSchemaFieldTableRowProps): JSX.Element {
  const additionalIntermediateLookupStepTypes = [];
  if (
    props.schema?.__typename === 'ProcessOutputSchemaNode' &&
    props.schema.processExecutionType === ProcessExecutionType.ENTRY
  ) {
    additionalIntermediateLookupStepTypes.push(LookupStepType.FILTER_BY_ORIGIN);
  }
  return (
    <FieldTableRow
      {...props}
      availableFieldOptions={getFieldOptions({
        schemaFieldSelectors: props.schemaFieldSelectors,
        storableSchemas: props.storableSchemas,
        tagSchemas: props.tagSchemas,
        schemaInterfaces: props.schemaInterfaces,
        initialField: props.initialField,
        includeSnapshotField: true,
        includeBooleanField: true,
        snapshotFieldDisabledMessage: getSnapshotFieldDisabledMessage(props.schema, props.initialField),
      })}
      lookupConfigModalProps={getSnapshotFieldLookupConfigModalProps(
        props.schema,
        additionalIntermediateLookupStepTypes
      )}
      disableControlsWithTooltips={{
        [DisablableControl.DELETE_BUTTON]: props.isFieldBeingCreated ? null : DISALLOW_DELETE_SCHEMA_FIELDS,
      }}
      isUneditable={props.initialField.isUneditable}
      shouldShowSubmissionWarningOnCreate={true}
      schemaId={props.schema?.id ?? null}
      shouldShowTooltipText={true}
    />
  );
}
